/**
 * Signed, versioned, offline-cacheable configuration — THE DEVICE HALF.
 *
 * Deliberately free of anything server-side. The publishing functions live in
 * ./publish because they carry Postgres DDL, and a single module exporting both
 * dragged `createConfigTables` into the POS bundle — 8 kB of SQL a browser can
 * never execute, on a budget that exists because the till starts on a cheap
 * Android tablet. Tree-shaking does not save you: a `CREATE TABLE` template
 * literal is not obviously side-effect-free to a bundler.
 *
 * Specification: ../../test/config.spec.ts. Master plan §3 K8, §3 K4.
 *
 * A till's configuration cannot be fetched when it is needed, because the
 * moment it is needed is the moment the WAN is down. So it is cached on the
 * device — and therefore it must be signed, because a cache on a restaurant LAN
 * whose Wi-Fi password is printed on the menu is otherwise an instruction
 * channel for anyone in the dining room.
 *
 * Signed alone is not enough. A bundle stays genuinely signed forever, so
 * without a version and an expiry, replaying last month's config is a
 * valid-looking way to undo a change: restore a revoked printer, reinstate an
 * old price list, lower a version gate. Hence monotonic versions, and an expiry
 * the device enforces for itself.
 */

import { canonicalBytes, sign, verify } from "../crypto";

export interface ConfigBundle {
  readonly tenantId: string;
  readonly venueId: string;
  /** Monotonic. A device never accepts an older one. */
  readonly version: number;
  readonly issuedAt: string;
  /** Past this the device runs STALE: still selling, with a banner. */
  readonly expiresAt: string;
  /** Past this it refuses new transactions. */
  readonly graceUntil: string;
  /** Opaque to the kernel, and covered by the signature. */
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface SignedConfigBundle {
  readonly bundle: ConfigBundle;
  /** Ed25519 over the bundle's canonical bytes, base64url. */
  readonly signature: string;
}

export async function signConfigBundle(
  bundle: ConfigBundle,
  privateKey: CryptoKey,
): Promise<SignedConfigBundle> {
  return { bundle, signature: await sign(canonicalBytes(bundle), privateKey) };
}

export type ConfigRejection =
  | "BAD_SIGNATURE"
  | "WRONG_TENANT"
  | "WRONG_VENUE"
  | "STALE_VERSION"
  | "PAST_GRACE";

export interface AcceptResult {
  readonly accepted: boolean;
  readonly reason?: ConfigRejection;
}

/**
 * What the device may do right now.
 *
 * CURRENT before expiry; STALE between expiry and grace, still selling with a
 * banner; EXPIRED past grace, refusing new transactions; ABSENT when nothing
 * has ever been cached.
 */
export type ConfigState = "ABSENT" | "CURRENT" | "STALE" | "EXPIRED";

export interface ConfigPosture {
  readonly state: ConfigState;
  readonly version: number | null;
  /** Epoch ms, or null when nothing is cached. Never zero — see `posture`. */
  readonly expiresAt: number | null;
  readonly graceUntil: number | null;
  readonly canStartTransaction: boolean;
  /**
   * Always true.
   *
   * Config governs what a device may START. It has no business deciding whether
   * money already taken reaches the books, and a device that refused to upload
   * a shift's takings because its config lapsed would have converted an
   * administrative problem into a financial one.
   */
  readonly canDrainOutbox: true;
  readonly bannerKey: string | null;
}

export interface CacheIdentity {
  readonly tenantId: string;
  readonly venueId: string;
}

export class ConfigCache {
  readonly #rootKey: CryptoKey;
  readonly #identity: CacheIdentity;
  #bundle: ConfigBundle | null = null;

  private constructor(rootKey: CryptoKey, identity: CacheIdentity) {
    this.#rootKey = rootKey;
    this.#identity = identity;
  }

  static create(rootKey: CryptoKey, identity: CacheIdentity): ConfigCache {
    return new ConfigCache(rootKey, identity);
  }

  current(): ConfigBundle | null {
    return this.#bundle;
  }

  /**
   * Verify and install, entirely locally.
   *
   * The signature is checked FIRST, so every field examined afterwards is one
   * the platform actually asserted rather than one an attacker supplied — and a
   * bundle that is both forged and misaddressed reports the forgery, which is
   * the finding somebody needs to see.
   */
  async accept(signed: SignedConfigBundle, now: Date): Promise<AcceptResult> {
    const ok = await verify(canonicalBytes(signed.bundle), signed.signature, this.#rootKey);
    if (!ok) return { accepted: false, reason: "BAD_SIGNATURE" };

    const bundle = signed.bundle;
    if (bundle.tenantId !== this.#identity.tenantId) {
      return { accepted: false, reason: "WRONG_TENANT" };
    }
    if (bundle.venueId !== this.#identity.venueId) {
      return { accepted: false, reason: "WRONG_VENUE" };
    }

    // Equal versions are idempotent: devices re-fetch on reconnect and on
    // restart, and treating that as an attack fills the log with noise — which
    // is how a real finding gets missed.
    if (this.#bundle && bundle.version < this.#bundle.version) {
      return { accepted: false, reason: "STALE_VERSION" };
    }

    // Dead on arrival. Installing it would replace a bundle that may still be
    // current, and take a trading venue off the air to do it.
    if (now.getTime() > Date.parse(bundle.graceUntil)) {
      return { accepted: false, reason: "PAST_GRACE" };
    }

    this.#bundle = bundle;
    return { accepted: true };
  }

  posture(now: Date): ConfigPosture {
    const bundle = this.#bundle;
    if (!bundle) {
      return {
        state: "ABSENT",
        version: null,
        // NULL, never zero. Zero is a real instant in 1970 and sellState
        // compares with `>`, so a zero would read as "grace ended 56 years ago"
        // and take the venue off the air.
        expiresAt: null,
        graceUntil: null,
        canStartTransaction: false,
        canDrainOutbox: true,
        bannerKey: "config.absent",
      };
    }

    const expiresAt = Date.parse(bundle.expiresAt);
    const graceUntil = Date.parse(bundle.graceUntil);
    const at = now.getTime();

    // Boundaries are inclusive of the named instant: a bundle is current AT
    // expiresAt and stale AT graceUntil. Stated once, here, so the three states
    // cannot overlap or leave a gap.
    const state: ConfigState = at <= expiresAt ? "CURRENT" : at <= graceUntil ? "STALE" : "EXPIRED";

    return {
      state,
      version: bundle.version,
      expiresAt,
      graceUntil,
      canStartTransaction: state !== "EXPIRED",
      canDrainOutbox: true,
      bannerKey: state === "CURRENT" ? null : state === "STALE" ? "config.stale" : "config.expired",
    };
  }
}
